import { useEffect, useMemo, useState } from "react";
import Link from "next/link";

import { useSession } from "@/components/providers/session";
import KitSelectionModal from "@/components/online/kit-selection-modal";
import { currency } from "@/utils/formats";
import {
  basketLineTotalPence,
  basketWasPricePence,
  buildOptionBasketFieldUpdates,
  courseSlugFromBasketUrl,
  normalizeOnlineListingImageUrl,
} from "@/lib/online-basket-checkout";
import {
  formatPricePence,
  type OnlineCourseKitLine,
  type OnlineCourseOption,
} from "@/lib/online-course-pricing";
import {
  kitPricePence,
  optionHasKitPrompt,
} from "@/lib/online-course-kit-selection";

import type { Entry } from "@/components/checkout/online-course";

type BasketFields = Entry["fields"] & {
  configurationId?: string | null;
  optionCode?: string | null;
  optionLabel?: string | null;
  optionsManaged?: boolean;
  kitSelections?: OnlineCourseKitLine[];
  requiredKits?: OnlineCourseKitLine[];
};

export default function OnlineCourseBasketItem({
  onlineCourse,
}: {
  onlineCourse: Entry;
  itemIndex: number;
}) {
  const { removeOnlineCourses, updateOnlineCourse, setContext } = useSession();
  const fields = onlineCourse.fields as BasketFields;
  const [kitModalOpen, setKitModalOpen] = useState(false);
  const [options, setOptions] = useState<OnlineCourseOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const courseSlug = courseSlugFromBasketUrl(fields.url);
  const kitSelections = fields.kitSelections || [];
  const lineTotal = basketLineTotalPence(onlineCourse);
  const wasPricePence = basketWasPricePence(fields.data?.was);

  const selectedOption = useMemo(() => {
    if (!fields.configurationId) {
      return null;
    }

    return (
      options.find((option) => option.id === fields.configurationId) || null
    );
  }, [fields.configurationId, options]);

  const modalOption = useMemo<OnlineCourseOption | null>(() => {
    if (selectedOption) {
      return selectedOption;
    }

    if (!fields.configurationId) {
      return null;
    }

    return {
      id: fields.configurationId,
      parent_course_slug: courseSlug,
      option_code:
        (fields.optionCode as OnlineCourseOption["option_code"]) || "theory",
      option_label: fields.optionLabel || "Selected option",
      price_pence: fields.pricing?.unit_amount || 0,
      was_price_pence: wasPricePence,
      price_vat_mode: "vat_inclusive",
      is_default: false,
      display_order: 0,
      delivery_mode: "",
      certificate_release_mode: "",
      case_study_required: false,
      video_evidence_required: false,
      consultation_form_required: false,
      tutor_review_required: false,
      live_session_required: false,
      webinar_required: false,
      assessment_required: false,
      required_kits: fields.requiredKits || [],
      required_products: [],
    };
  }, [courseSlug, fields, selectedOption, wasPricePence]);

  const hasKitPrompt = optionHasKitPrompt(
    modalOption || { required_kits: fields.requiredKits || [] },
  );

  const applyOption = (
    option: OnlineCourseOption,
    nextKitSelections: OnlineCourseKitLine[] = [],
  ) => {
    updateOnlineCourse(
      fields.url,
      buildOptionBasketFieldUpdates(
        option,
        nextKitSelections,
        fields.data as unknown as Record<string, unknown>,
      ),
    );
  };

  useEffect(() => {
    if (!courseSlug) {
      return;
    }

    let cancelled = false;
    setOptionsLoading(true);

    fetch(`/api/online-courses/${courseSlug}/options`)
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) {
          return;
        }

        setOptions(Array.isArray(data.options) ? data.options : []);
      })
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setOptionsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  useEffect(() => {
    if (options.length !== 1 || fields.configurationId) {
      return;
    }

    applyOption(options[0], []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, fields.configurationId]);

  const handleOptionChange = (optionId: string) => {
    const option = options.find((item) => item.id === optionId);
    if (!option || option.id === fields.configurationId) {
      return;
    }

    applyOption(option, []);
  };

  const handleKitConfirm = (
    option: OnlineCourseOption,
    selections: OnlineCourseKitLine[],
  ) => {
    applyOption(option, selections);
    setKitModalOpen(false);
  };

  const listingImageUrl = normalizeOnlineListingImageUrl(
    fields.listingImage?.fields?.file?.url,
  );

  return (
    <>
      {modalOption ? (
        <KitSelectionModal
          open={kitModalOpen}
          option={modalOption}
          courseTitle={fields.title}
          actionLabel="Save selection"
          initialKitSelections={kitSelections}
          onClose={() => setKitModalOpen(false)}
          onConfirm={handleKitConfirm}
        />
      ) : null}

      <div className="py-4 flex space-x-6">
        {listingImageUrl && (
          <div className="relative flex-none w-16 h-16 md:w-24 md:h-24 bg-gray-100 rounded-md">
            <img
              alt={fields.listingImage?.fields?.title || fields.title}
              src={listingImageUrl}
              className="w-full object-cover h-full bg-gray-100 rounded-md"
            />
          </div>
        )}
        <div className="flex-auto flex flex-col">
          <div className="sm:space-y-1 flex items-start sm:justify-between sm:space-x-6 flex-1">
            <div className="flex-auto text-sm font-medium space-y-2">
              <h3 className="text-gray-900">
                <Link
                  href={fields.url}
                  passHref
                  className="focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 rounded"
                >
                  {fields.title}
                </Link>
              </h3>

              {options.length > 1 ? (
                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Training option
                  </span>
                  <select
                    value={fields.configurationId || ""}
                    disabled={optionsLoading}
                    onChange={(event) => handleOptionChange(event.target.value)}
                    className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  >
                    <option value="" disabled>
                      {optionsLoading ? "Loading options..." : "Choose an option"}
                    </option>
                    {options.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.option_label} ({formatPricePence(option.price_pence)})
                      </option>
                    ))}
                  </select>
                </label>
              ) : fields.optionLabel ? (
                <p className="text-sm text-brand-700">
                  Option: {fields.optionLabel}
                </p>
              ) : optionsLoading ? (
                <p className="text-sm text-gray-500">Loading options...</p>
              ) : null}

              {kitSelections.length > 0 ? (
                <ul className="space-y-1 text-sm text-gray-600">
                  {kitSelections.map((kit) => (
                    <li key={`${fields.url}-${kit.name}`}>
                      + {kit.name} ({formatPricePence(kitPricePence(kit))})
                    </li>
                  ))}
                </ul>
              ) : hasKitPrompt ? (
                <p className="text-sm text-gray-500">No training kit selected</p>
              ) : null}

              <div className="flex justify-between gap-4">
                <p className="text-brand-600 block font-semibold">
                  {currency(lineTotal)}
                </p>

                {wasPricePence ? (
                  <p className="text-sm text-gray-500 block">
                    Was: {currency(wasPricePence)}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {hasKitPrompt && fields.configurationId ? (
              <button
                type="button"
                onClick={() => setKitModalOpen(true)}
                className="relative font-medium flex items-center justify-center py-1 px-2.5 border border-brand-200 text-xs rounded-md text-brand-700 bg-brand-50 shadow-sm transition-all duration-200 hover:border-brand-400 hover:bg-brand-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
              >
                {kitSelections.length > 0 ? "Change kits" : "Choose kits"}
              </button>
            ) : null}

            {options.length <= 1 ? (
              <Link
                href={fields.url}
                type="button"
                onClick={() => {
                  setContext((s) => ({ ...s, onlineBasket: false }));
                }}
                className="relative font-medium flex items-center justify-center py-1 px-2.5 border border-gray-300 text-xs rounded-md text-gray-800 bg-white shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:border-gray-300 focus:bg-gray-200 focus:text-gray-900"
              >
                Change option
              </Link>
            ) : null}

            <button
              type="button"
              onClick={() => removeOnlineCourses(fields.url)}
              className="relative font-medium flex items-center justify-center py-1 px-2.5 border border-gray-300 text-xs rounded-md text-gray-800 bg-white shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:border-gray-300 focus:bg-gray-200 focus:text-gray-900"
            >
              <span className="text-red-600">Remove</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export type { Entry };
